import { Injectable, OnDestroy, inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CareerRoleActivationStatus } from '../../../../models';

import { CareerRoleFullPictureStore } from './career-role-full-picture.store';

const POLL_MS = 30_000;
const TIMEOUT_MS = 2 * 60 * 60 * 1000;

interface WatchedActivation {
  roleSlug: string;
  kind: 'role' | 'specialization';
  specializationSlug?: string;
  status: CareerRoleActivationStatus;
  startedAt: number;
  onPromoted?: () => void;
}

@Injectable({ providedIn: 'root' })
export class ActivationStatusWatcherService implements OnDestroy {
  private readonly pictureStore = inject(CareerRoleFullPictureStore);
  private readonly snackBar = inject(MatSnackBar);
  private readonly watched = new Map<string, WatchedActivation>();
  private timer: ReturnType<typeof setInterval> | null = null;

  ngOnDestroy(): void {
    this.stop();
  }

  watchRole(roleSlug: string, status: CareerRoleActivationStatus, onPromoted?: () => void): void {
    if (status !== 'activating') {
      return;
    }
    this.watched.set(`role:${roleSlug}`, {
      roleSlug,
      kind: 'role',
      status,
      startedAt: Date.now(),
      onPromoted
    });
    this.ensureTimer();
  }

  watchSpecialization(
    roleSlug: string,
    specializationSlug: string,
    status: CareerRoleActivationStatus,
    onPromoted?: () => void
  ): void {
    if (status !== 'activating') {
      return;
    }
    this.watched.set(`spec:${roleSlug}:${specializationSlug}`, {
      roleSlug,
      kind: 'specialization',
      specializationSlug,
      status,
      startedAt: Date.now(),
      onPromoted
    });
    this.ensureTimer();
  }

  private ensureTimer(): void {
    if (!this.timer) {
      this.timer = setInterval(() => void this.poll(), POLL_MS);
    }
  }

  private stop(): void {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  private async poll(): Promise<void> {
    if (!this.watched.size) {
      this.stop();
      return;
    }
    const now = Date.now();
    for (const [key, entry] of [...this.watched.entries()]) {
      if (now - entry.startedAt > TIMEOUT_MS) {
        this.watched.delete(key);
        this.snackBar.open(
          `Still activating: ${entry.roleSlug}. Check background jobs.`,
          'Close',
          { duration: 6000 }
        );
        continue;
      }
      try {
        const picture = await this.pictureStore.load(entry.roleSlug, { background: true });
        if (!picture) {
          continue;
        }
        if (entry.kind === 'role') {
          const status = picture.activationStatus ?? (picture.active ? 'active' : 'inactive');
          if (status === 'active') {
            this.watched.delete(key);
            this.snackBar.open(`${entry.roleSlug} is now active`, 'Close', { duration: 4000 });
            entry.onPromoted?.();
          }
        } else if (entry.specializationSlug) {
          const spec = picture.specializations.find((s) => s.slug === entry.specializationSlug);
          const status = spec?.activationStatus ?? (spec?.active ? 'active' : 'inactive');
          if (status === 'active') {
            this.watched.delete(key);
            this.snackBar.open(`${entry.specializationSlug} is now active`, 'Close', { duration: 4000 });
            entry.onPromoted?.();
          }
        }
      } catch {
        // ignore transient errors
      }
    }
    if (!this.watched.size) {
      this.stop();
    }
  }
}